import React, { useState, useRef, useEffect } from "react";
import PanelState from "../MainComponents/PanelState";
import { describeState } from "../../Helpers/BillingSyncLabels";

/**
 * Task 37, B5 — switching sync on and off, and discarding work before it is sent.
 *
 * Presentational. Readiness is decided by the SERVER and simply rendered here: working it out again
 * in the browser would let this screen offer a switch the server then refuses, which reads as a
 * fault rather than as a rule.
 *
 * NO MODAL. Discarding asks for confirmation in the page, as the client asked — a dialog that
 * vanishes when somebody clicks beside it is the pattern we were told to move away from.
 *
 * Task 37 client demo feedback. Nothing listed here is sent until the reseller queues it for
 * syncing (N1), so each line now shows whether it is still awaiting approval. Each table can be
 * selected whole (N2). Discarded lines can be shown rather than only counted (N3), and restored a
 * chosen few at a time rather than all at once (N4).
 *
 * "Queue for syncing" is here, beside the lines it sends, at the client's request after testing on
 * Demo. It still queues THE PREVIEW, not whatever is listed: the reseller checks the figures on the
 * Preview tab, and only the lines in a current preview are approved. The server insists on the same,
 * so the button only says ahead of time what a click would be told.
 */
export default function ActivationPanel({
    state,
    pendingWork,
    busy,
    onEnable,
    onDisable,
    onDiscard,
    onRebuild,
    preview,
    previewLoadFailed,
    onQueue,
    showDiscarded,
    onToggleDiscarded,
    loading,
    error,
    onRetry,
    // A master administrator working on another reseller: every line stays readable, and Switch
    // off stays available, but nothing can be selected, discarded or rebuilt. The server refuses
    // those; here nothing offers them.
    readOnly,
}) {
    const [selected, setSelected] = useState([]);
    const [confirming, setConfirming] = useState(false);
    // N4 — chosen discarded lines. Separate from `selected`, which is work still waiting: the two
    // tables lead to opposite actions and must never share a selection.
    const [restoreSelected, setRestoreSelected] = useState([]);

    // U5 — turning "Show discarded" on brings the Discarded table into view, but only once its lines
    // have arrived: the toggle refetches, so at the moment of the click there is nothing to scroll
    // to. The flag is set ONLY by the user switching it on, so a refresh, a poll or a reseller switch
    // never moves the page.
    //
    // Declared above the early return below, as every hook here must be, or React's hook order
    // changes between the loading render and the loaded one.
    //
    // ONE ANSWER, WHATEVER IT IS. The flag is spent on the first response after the click — the
    // pending work the toggle refetched, or the error it failed with — so a refetch that comes back
    // empty, or fails, cannot leave it armed for some later refresh to scroll the page unprompted.
    // `revealFrom` is the pending work on screen at the click: anything else is the new answer.
    // `revealError` is the last error seen, so an error left over from an earlier load is not
    // mistaken for this refetch failing — only a new one, after the page has cleared it, counts.
    const revealDiscarded = useRef(false);
    const revealFrom = useRef(null);
    const revealError = useRef(null);
    const discardedHeading = useRef(null);
    useEffect(() => {
        if (!revealDiscarded.current) return;
        const errorChanged = error !== revealError.current;
        revealError.current = error;
        if (errorChanged && error) {
            revealDiscarded.current = false;
            return;
        }
        if (pendingWork === revealFrom.current) return;
        revealDiscarded.current = false;
        const rows = (pendingWork && pendingWork.discarded && pendingWork.discarded.length) || 0;
        if (!showDiscarded || rows === 0) return;
        const heading = discardedHeading.current;
        if (!heading) return;
        // Scrolls the nearest scrolling ancestor, which on these screens is .billing-sync-content
        // rather than the window.
        heading.scrollIntoView({ behavior: "smooth", block: "start" });
        // Focus follows, for keyboard and screen-reader users, without a second jump.
        heading.focus({ preventScroll: true });
    }, [showDiscarded, pendingWork, error]);

    // This used to be `if (!state) return "Loading…"` — a null check standing in for a loading
    // check, which meant a FAILED load showed a spinner for ever with nothing to retry. The real
    // loading and error states now come from the page, and a null state with neither is a load
    // that has not started rather than one that has silently died.
    if (!state) {
        return (
            <PanelState
                busy={loading}
                error={error}
                actionLabel={error ? "Try again" : undefined}
                busyLabel="Trying…"
                onAction={error ? onRetry : undefined}
            />
        );
    }

    const charges = (pendingWork && pendingWork.charges) || [];
    const cancellations = (pendingWork && pendingWork.cancellations) || [];
    const discardedCount = (pendingWork && pendingWork.discardedCount) || 0;
    const discarded = (pendingWork && pendingWork.discarded) || [];

    function toggle(id) {
        setSelected((current) =>
            current.includes(id) ? current.filter((x) => x !== id) : current.concat(id));
        setConfirming(false);
    }

    // N2 — a whole table at once. Per table rather than one control for everything: cancellations
    // are never selected as a side effect of selecting charges, because discarding one leaves a
    // customer who has left being billed.
    function toggleAll(ids, select) {
        setSelected((current) => select
            ? current.concat(ids.filter((id) => !current.includes(id)))
            : current.filter((id) => !ids.includes(id)));
        setConfirming(false);
    }

    function toggleRestore(id) {
        setRestoreSelected((current) =>
            current.includes(id) ? current.filter((x) => x !== id) : current.concat(id));
    }

    function toggleRestoreAll(ids, select) {
        setRestoreSelected((current) => select
            ? current.concat(ids.filter((id) => !current.includes(id)))
            : current.filter((id) => !ids.includes(id)));
    }

    return (
        <div>
            {/* U1 — full width, so it lines up with the tables below. */}
            <div className="border rounded p-3 mb-4">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <div className="fw-semibold">
                            Sync is {state.enabled ? "on" : "off"}
                        </div>
                        <div className="text-muted" style={{ fontSize: 14 }}>
                            {state.enabled
                                ? "Billing lines are sent to ConnectWise on the scheduled run."
                                : "Nothing is being sent to ConnectWise."}
                        </div>
                    </div>

                    {state.enabled ? (
                        <button
                            type="button"
                            className="btn btn-outline-danger"
                            disabled={busy || !!state.disableBlockedReason}
                            onClick={onDisable}
                        >
                            Switch off
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={busy || !state.canEnable}
                            onClick={onEnable}
                        >
                            Switch on
                        </button>
                    )}
                </div>

                {state.disableBlockedReason && state.enabled && (
                    <div className="alert alert-info py-2 mt-3 mb-0" style={{ fontSize: 14 }}>
                        {state.disableBlockedReason}
                    </div>
                )}

                {!state.enabled && state.blockers && state.blockers.length > 0 && (
                    <div className="alert alert-warning py-2 mt-3 mb-0" style={{ fontSize: 14 }}>
                        <div className="fw-semibold mb-1">Before sync can be switched on:</div>
                        <ul className="mb-0">
                            {state.blockers.map((b) => <li key={b}>{b}</li>)}
                        </ul>
                    </div>
                )}

                <div className="d-flex gap-4 mt-3 text-muted" style={{ fontSize: 13 }}>
                    <span>{state.runningCount || 0} running</span>
                    <span>{state.pendingCount || 0} waiting</span>
                </div>
            </div>

            {/* U4 — the toggle sits on this heading rather than beside "Charges": this heading always
                renders, while the Charges table does not exist when every charge has been discarded,
                which is exactly when the toggle is needed. It also covers both tables equally. Only
                offered while something is discarded (N12), and kept in the read-only support view. */}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Waiting to be sent</h6>
                {discardedCount > 0 && (
                    <div className="form-check form-switch">
                        <input
                            id="cws-show-discarded"
                            type="checkbox"
                            role="switch"
                            className="form-check-input"
                            checked={!!showDiscarded}
                            disabled={busy}
                            onChange={(e) => {
                                setRestoreSelected([]);
                                revealDiscarded.current = e.target.checked;
                                revealFrom.current = pendingWork;
                                revealError.current = error;
                                onToggleDiscarded(e.target.checked);
                            }}
                        />
                        <label className="form-check-label" htmlFor="cws-show-discarded">
                            Show discarded ({discardedCount})
                        </label>
                    </div>
                )}
            </div>
            <p className="text-muted" style={{ maxWidth: 820 }}>
                {readOnly
                    ? "This reseller's billing lines. Nothing here is sent until the reseller queues it for syncing. Only the reseller can discard or rebuild lines."
                    : "Nothing here is sent until you queue it for syncing below, after checking the figures on the Preview tab. Anything here can be thrown away before it reaches ConnectWise. Discarding is not permanent for the figures — they can be rebuilt from the usage they came from."}
            </p>

            <QueueForSync
                state={state}
                preview={preview}
                previewLoadFailed={previewLoadFailed}
                busy={busy}
                onQueue={onQueue}
            />

            {/* N5 — work that exists but was discarded must not look like work never calculated. */}
            {charges.length === 0 && cancellations.length === 0 && discardedCount > 0 && (
                <div className="alert alert-info py-2" style={{ fontSize: 14 }}>
                    Nothing is waiting to be sent. {discardedCount} calculated line(s) were discarded —
                    turn on "Show discarded" above to see them
                    {readOnly ? "." : " and rebuild any you need."}
                </div>
            )}

            <WorkTable
                title="Charges"
                caption="Throwing one of these away means the customer is not billed for that month."
                lines={charges}
                selected={selected}
                disabled={busy}
                selectable={!readOnly}
                onToggle={toggle}
                onToggleAll={toggleAll}
                showState
            />

            <WorkTable
                title="Cancellations"
                caption="These end billing for customers who have left. Throwing one away leaves that customer billed until somebody notices."
                lines={cancellations}
                selected={selected}
                disabled={busy}
                selectable={!readOnly}
                onToggle={toggle}
                onToggleAll={toggleAll}
                showState
                tone="warning"
            />

            {/* U3 — table actions are right-aligned, like every other action on this tab. */}
            {!readOnly && (
            <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
                {confirming ? (
                    <>
                        {/* In the page, deliberately. Not a dialog. */}
                        <span className="text-danger" style={{ fontSize: 14 }}>
                            Discard {selected.length} line(s)?
                        </span>
                        <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            disabled={busy}
                            onClick={() => {
                                onDiscard(selected);
                                setSelected([]);
                                setConfirming(false);
                            }}
                        >
                            Yes, discard
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            disabled={busy}
                            onClick={() => setConfirming(false)}
                        >
                            Keep them
                        </button>
                    </>
                ) : (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={busy || selected.length === 0}
                        onClick={() => setConfirming(true)}
                    >
                        Discard selected
                    </button>
                )}
            </div>
            )}

            {showDiscarded && discarded.length > 0 && (
                <div className="mt-4">
                    <WorkTable
                        headingRef={discardedHeading}
                        title="Discarded"
                        caption="Not sent, and not waiting to be. Rebuilding a line puts it back to await your approval — nothing is sent until you queue it for syncing."
                        lines={discarded}
                        selected={restoreSelected}
                        disabled={busy}
                        selectable={!readOnly}
                        onToggle={toggleRestore}
                        onToggleAll={toggleRestoreAll}
                        showState
                        markCancellations
                    />

                    {/* N4 — the chosen lines, the same way Discard takes them. Not confirmed in the
                        page: rebuilding sends nothing, it only puts lines back in front of the
                        reseller. */}
                    {!readOnly && (
                        <div className="d-flex justify-content-end">
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            disabled={busy || restoreSelected.length === 0}
                            onClick={() => {
                                onRebuild(restoreSelected);
                                setRestoreSelected([]);
                            }}
                        >
                            Rebuild selected
                        </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * Task 37 N1 — the approval step, and why it cannot be taken yet when it cannot.
 *
 * Disabled rather than hidden, so the reseller can see the step exists. Every reason it is disabled
 * says what to do about it, and each is a refusal the server would give anyway.
 */
function QueueForSync({ state, preview, previewLoadFailed, busy, onQueue }) {
    const { canQueue, reason } = describeQueue(state, preview, previewLoadFailed);

    return (
        <div className="border rounded p-3 mb-4 d-flex justify-content-between align-items-center gap-3">
            <div className={canQueue ? undefined : "text-muted"} style={{ fontSize: 14 }}>{reason}</div>
            <button
                type="button"
                className="btn btn-primary text-nowrap"
                disabled={busy || !canQueue}
                onClick={onQueue}
            >
                {/* Not "Queueing…" while busy: this tab has one busy flag for every action, and a
                    discard in progress is not a queue. */}
                Queue for syncing
            </button>
        </div>
    );
}

function describeQueue(state, preview, previewLoadFailed) {
    if (state.isActingOnBehalf) {
        return { canQueue: false, reason: "Only the reseller can queue their own billing for syncing." };
    }
    // Ahead of every preview check: after a failed read, `preview` is either nothing or the last one
    // seen, and neither says anything true about the preview as it is now.
    if (previewLoadFailed) {
        return {
            canQueue: false,
            reason: "Your preview could not be checked just now, so it cannot be queued. Open the Preview tab to try again.",
        };
    }
    if (preview && preview.isPreparing) {
        return { canQueue: false, reason: "A preview is being prepared. Check it on the Preview tab once it is ready." };
    }
    if (!preview || !preview.isPrepared) {
        return {
            canQueue: false,
            reason: preview && preview.hasExpired
                ? "Your last preview has expired. Prepare a new one on the Preview tab and check it before queueing."
                : "Prepare a preview on the Preview tab and check it before queueing.",
        };
    }
    if (preview.isCurrent === false) {
        return {
            canQueue: false,
            reason: "Your billing figures have changed since the preview was prepared. Prepare it again on the Preview tab and check it before queueing.",
        };
    }
    const count = preview.awaitingApprovalCount || 0;
    if (count === 0) {
        return { canQueue: false, reason: "Nothing in your latest preview is awaiting approval." };
    }
    const preparedAt = preview.preparedAt ? new Date(preview.preparedAt).toLocaleString() : "";
    return {
        canQueue: true,
        reason: `Queues the ${count} line(s) awaiting approval in your preview prepared ${preparedAt}. Check them on the Preview tab first.`,
    };
}

/**
 * Charges and cancellations get their own table rather than one list with a column.
 *
 * They are not the same act: discarding a charge under-bills a customer for a month, while
 * discarding a cancellation leaves somebody who has left being billed indefinitely. Nothing is
 * selected by default in either, and the cancellations table says what it costs to get it wrong.
 */
function WorkTable({
    title, caption, lines, selected, disabled, onToggle, onToggleAll, tone, showState, markCancellations,
    // False in the master administrator's support view: no checkbox column, so nothing suggests
    // these lines can be acted on.
    selectable = true,
    // U5 — given only to the Discarded table, whose title is scrolled to and focused when the
    // reseller turns "Show discarded" on.
    headingRef,
}) {
    if (lines.length === 0) return null;

    const ids = lines.map((line) => line.billingSyncItemId);
    const allSelected = ids.every((id) => selected.includes(id));

    return (
        <div className="mb-3">
            <div
                className="fw-semibold"
                ref={headingRef}
                tabIndex={headingRef ? -1 : undefined}
                style={headingRef ? { outline: "none", scrollMarginTop: 8 } : undefined}
            >
                {title}
            </div>
            <div className={`text-muted mb-2${tone ? ` text-${tone}` : ""}`} style={{ fontSize: 13 }}>
                {caption}
            </div>
            {/* U2 — one fixed column layout shared by every table on this tab (general.css), so a
                column starts in the same place in Charges, Cancellations and Discarded instead of
                each table sizing itself from its own content. */}
            <table className="table table-sm align-middle billing-sync-lines">
                <colgroup>
                    {selectable && <col className="bsl-select" />}
                    <col className="bsl-customer" />
                    <col className="bsl-type" />
                    <col className="bsl-period" />
                    <col className="bsl-number" />
                    <col className="bsl-number" />
                    {showState && <col />}
                </colgroup>
                <thead>
                    <tr>
                        {selectable && (
                            <th>
                                {/* N2 — this table, whole. Never every table at once. */}
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    aria-label={`Select all ${title.toLowerCase()}`}
                                    disabled={disabled}
                                    checked={allSelected}
                                    onChange={() => onToggleAll(ids, !allSelected)}
                                />
                            </th>
                        )}
                        <th>Customer</th>
                        <th>Type</th>
                        <th>Period</th>
                        <th className="text-end">Quantity</th>
                        <th className="text-end">Unit price</th>
                        {showState && <th className="bsl-state">State</th>}
                    </tr>
                </thead>
                <tbody>
                    {lines.map((line) => (
                        <tr key={line.billingSyncItemId}>
                            {selectable && (
                                <td>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        disabled={disabled}
                                        checked={selected.includes(line.billingSyncItemId)}
                                        onChange={() => onToggle(line.billingSyncItemId)}
                                    />
                                </td>
                            )}
                            <td>{line.customerLabel}</td>
                            <td>
                                {line.additionType === "STORAGE" ? "Storage" : "Licence"}
                                {/* A discarded cancellation carries no quantity or price, so without
                                    this it would read as a zero charge — when rebuilding it would
                                    actually end that customer's billing in ConnectWise. */}
                                {markCancellations && line.isCancellation && (
                                    <span className="text-warning"> — ends billing</span>
                                )}
                            </td>
                            <td>{line.periodMonth}/{line.periodYear}</td>
                            <td className="text-end">{line.quantity}</td>
                            <td className="text-end">{line.unitPrice}</td>
                            {/* N1 — whether this line is still waiting for the reseller's approval. */}
                            {showState && <td className="bsl-state">{describeState({ itemStatus: line.status }).label}</td>}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
